import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldHalved } from "@fortawesome/free-solid-svg-icons";
import styles from "./WatchSignInCodeCard.module.css";

/**
 * Watch Configuration card shown only after a sign-in code is generated.
 * Does not include the Enable Sign-In Code toggle.
 */
const WatchSignInCodeCard = ({
  code,
  expiresInSeconds = 300,
  generating = false,
  onGenerate,
  onExpire,
}) => {
  const [remainingSeconds, setRemainingSeconds] = useState(expiresInSeconds);

  useEffect(() => {
    if (!code) {
      setRemainingSeconds(expiresInSeconds);
      return undefined;
    }

    setRemainingSeconds(expiresInSeconds);
    const intervalId = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          window.clearInterval(intervalId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [code, expiresInSeconds]);

  useEffect(() => {
    if (code && remainingSeconds === 0) {
      onExpire?.();
    }
  }, [code, remainingSeconds, onExpire]);

  const hasActiveWatchCode = Boolean(code) && remainingSeconds > 0;
  const codeDigits = useMemo(
    () => String(code || "").slice(0, 6).padEnd(6, "").split(""),
    [code]
  );
  const expiryMinutes = Math.max(1, Math.ceil(remainingSeconds / 60));

  if (!code) return null;

  return (
      <div className={styles.codeArea}>
        <div className={styles.verificationCard}>
          <div className={styles.verificationIconWrap}>
            <FontAwesomeIcon
              icon={faShieldHalved}
              className={styles.verificationIcon}
            />
          </div>
          <div className={styles.verificationTitle}>Sign-In Code</div>
          <div className={styles.verificationDigitsRow}>
            {codeDigits.map((digit, idx) => (
              <div key={`${digit}-${idx}`} className={styles.verificationDigit}>
                {digit || ""}
              </div>
            ))}
          </div>
          <div className={styles.codeStatusRow}>
            <span
              className={`${styles.codeStatusDot} ${
                hasActiveWatchCode
                  ? styles.codeStatusDotActive
                  : styles.codeStatusDotExpired
              }`}
            />
            <span
              className={`${styles.codeStatusText} ${
                hasActiveWatchCode
                  ? styles.codeStatusActive
                  : styles.codeStatusExpired
              }`}
            >
              {hasActiveWatchCode
                ? `Expires in ${expiryMinutes} minute${
                    expiryMinutes > 1 ? "s" : ""
                  }`
                : "Code expired"}
            </span>
          </div>
          <div className={styles.codeActionsRow}>
            <button
              type="button"
              className={styles.codeActionBtn}
              onClick={onGenerate}
              disabled={generating || hasActiveWatchCode}
            >
              {generating ? "Generating..." : "New code"}
            </button>
          </div>
          {hasActiveWatchCode ? (
            <div className={styles.newCodeHint}>
              New code will be enabled after current code expires.
            </div>
          ) : null}
        </div>
      </div>
  );
};

WatchSignInCodeCard.propTypes = {
  code: PropTypes.string,
  expiresInSeconds: PropTypes.number,
  generating: PropTypes.bool,
  onGenerate: PropTypes.func,
  onExpire: PropTypes.func,
};

export default WatchSignInCodeCard;
